// Card creation requests (TT-7449 / back TT-7448).
//
// A user who may hold a card but not create one asks a referent for a flexible
// card; every holder of `manage_all_payment_cards` in the company may approve
// (by creating the card) or decline it.
import {callAPI} from '@/store/utils';

const FYGR_COMPANY = 'FYGR_COMPANY';

export interface CardRequestItem {
  id: string;
  company_id?: string;
  requester_user_id?: string;
  requester_email?: string | null;
  card_type?: string;
  name?: string;
  reason?: string | null;
  status?: string;
  creation_date?: string;
}

export interface CardReferent {
  user_id: string;
  email: string;
}

interface CardRequestsState {
  // The current user's own request, while it awaits a decision.
  myPendingRequest: CardRequestItem | null;
  // Requests awaiting this referent's decision (empty for a plain user).
  pendingRequests: CardRequestItem[];
  // Who may handle a request in this company — the requester addresses one.
  referents: CardReferent[];
}

function companyIdOf(rootState: {companyId?: string}): string {
  return rootState.companyId || localStorage.getItem(FYGR_COMPANY) || '';
}

export default {
  namespaced: true,

  state: (): CardRequestsState => ({
    myPendingRequest: null,
    pendingRequests: [],
    referents: [],
  }),

  getters: {
    myPendingRequest: (state: CardRequestsState): CardRequestItem | null =>
      state.myPendingRequest,
    pendingRequests: (state: CardRequestsState): CardRequestItem[] =>
      state.pendingRequests,
    referents: (state: CardRequestsState): CardReferent[] => state.referents,
    pendingRequestById:
      (state: CardRequestsState) =>
      (requestId: string): CardRequestItem | null =>
        state.pendingRequests.find((r) => r.id === requestId) || null,
  },

  mutations: {
    setCardRequests(
      state: CardRequestsState,
      result: {
        my_pending_request?: CardRequestItem | null;
        pending_requests?: CardRequestItem[];
        referents?: CardReferent[];
      } | null,
    ) {
      state.myPendingRequest = result?.my_pending_request || null;
      state.pendingRequests = Array.isArray(result?.pending_requests)
        ? result.pending_requests
        : [];
      state.referents = Array.isArray(result?.referents)
        ? result.referents
        : [];
    },
    // Drop a decided request from the referent's list without a refetch.
    removePendingRequest(state: CardRequestsState, requestId: string) {
      state.pendingRequests = state.pendingRequests.filter(
        (r) => r.id !== requestId,
      );
    },
  },

  actions: {
    async actionGetCardRequests({commit, rootState}) {
      const companyId = companyIdOf(rootState);
      if (!companyId) return null;
      const res = await callAPI(
        'GET',
        `company/${companyId}/equals/card-requests`,
      );
      commit('setCardRequests', res?.result || null);
      return res;
    },

    async actionCreateCardRequest(
      {dispatch, rootState},
      {cardType, name, reason, referentUserId},
    ) {
      const res = await callAPI('POST', 'equals/card-requests', {
        company_id: companyIdOf(rootState),
        card_type: cardType,
        name,
        reason,
        referent_user_id: referentUserId || null,
      });
      await dispatch('actionGetCardRequests');
      return res;
    },

    async actionCancelCardRequest({dispatch, rootState}, {requestId}) {
      const companyId = companyIdOf(rootState);
      const res = await callAPI(
        'POST',
        `company/${companyId}/equals/card-requests/${requestId}/cancel`,
        {},
      );
      await dispatch('actionGetCardRequests');
      return res;
    },

    async actionRejectCardRequest(
      {commit, rootState},
      {requestId, decisionReason},
    ) {
      const companyId = companyIdOf(rootState);
      const res = await callAPI(
        'POST',
        `company/${companyId}/equals/card-requests/${requestId}/reject`,
        {decision_reason: decisionReason},
      );
      if (res?.error === false) {
        commit('removePendingRequest', requestId);
      }
      return res;
    },
  },
};
